import React, { useState } from 'react';
import { 
  TrendingUp, DollarSign, Users, Building, PlayCircle, Download, 
  AlertTriangle, CheckCircle, FileText, Calendar, Sparkles, 
  Database, CreditCard, Briefcase, Home, BarChart3, Target, Layers
} from 'lucide-react';

const FinanceConversion = ({ project }) => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedDomain, setSelectedDomain] = useState('gl');

  const runAnalysis = async () => {
    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/fce/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          project_id: project?.id || 'demo-project',
          use_mock_data: true,
          include_ai_narratives: true
        }),
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Analysis failed: ${response.statusText}`);
      }

      const data = await response.json();
      setAnalysis(data);
      setActiveTab('overview');
    } catch (error) {
      console.error('Error running finance conversion analysis:', error);
      alert('Failed to run analysis: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = () => {
    if (!analysis) return;
    
    const report = JSON.stringify(analysis, null, 2);
    const blob = new Blob([report], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `finance-conversion-${analysis.analysis_id}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const domainIcons = {
    gl: Database,
    co: BarChart3,
    copa: Target,
    aa: Home,
    ml: Layers,
    credit: CreditCard,
    bank: DollarSign,
    ap_ar: Briefcase
  };

  const domainNames = {
    gl: 'General Ledger (FI-GL)',
    co: 'Controlling (CO)',
    copa: 'Profitability Analysis (CO-PA)',
    aa: 'Asset Accounting (AA)',
    ml: 'Material Ledger (ML)',
    credit: 'Credit Management',
    bank: 'Bank Accounting',
    ap_ar: 'AP/AR & Business Partner'
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Building },
    { id: 'config', label: 'Configuration', icon: Database },
    { id: 'domains', label: 'Domain Assessments', icon: Target },
    { id: 'gaps', label: 'Gap Analysis', icon: AlertTriangle },
    { id: 'risks', label: 'Risk Profile', icon: AlertTriangle },
    { id: 'backlog', label: 'Conversion Backlog', icon: FileText },
    { id: 'waves', label: 'Wave Planning', icon: Calendar },
    { id: 'testing', label: 'Testing & Recon', icon: CheckCircle },
    { id: 'ai-insights', label: 'AI Insights', icon: Sparkles }
  ];

  if (!analysis) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-3xl font-bold mb-2">Finance Conversion Engine</h2>
          <p className="text-gray-600 mb-6">
            Comprehensive FI/CO/AA/CO-PA/ML analysis for S/4HANA finance conversion with AI-powered narratives
          </p>
          <button
            onClick={runAnalysis}
            disabled={loading}
            className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 disabled:opacity-50"
          >
            <PlayCircle className="w-5 h-5" />
            <span>{loading ? 'Running Analysis...' : 'Run Finance Conversion Analysis'}</span>
          </button>
        </div>

        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl shadow-md p-6">
          <h3 className="text-xl font-bold mb-4">What This Module Analyzes:</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-start space-x-3">
              <Database className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold">General Ledger (FI-GL)</h4>
                <p className="text-sm text-gray-600">Universal Journal migration, ledger setup, document splitting</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <BarChart3 className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold">Controlling (CO)</h4>
                <p className="text-sm text-gray-600">Cost elements to GL accounts, real-time FI-CO integration</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Target className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold">CO-PA</h4>
                <p className="text-sm text-gray-600">Account-based CO-PA migration, value field mapping</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Home className="w-6 h-6 text-orange-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold">Asset Accounting</h4>
                <p className="text-sm text-gray-600">New AA migration, depreciation alignment, ACDOCA posting</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Layers className="w-6 h-6 text-indigo-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold">Material Ledger</h4>
                <p className="text-sm text-gray-600">ML activation, actual costing, multi-currency valuation</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <CreditCard className="w-6 h-6 text-pink-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold">Credit Management</h4>
                <p className="text-sm text-gray-600">FSCM migration, BP integration, scoring engine</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <DollarSign className="w-6 h-6 text-teal-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold">Bank Accounting</h4>
                <p className="text-sm text-gray-600">Bank Account Management, DMEE redesign, EBS rules</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Briefcase className="w-6 h-6 text-rose-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold">AP/AR & BP</h4>
                <p className="text-sm text-gray-600">Business Partner migration, subledger integration</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-3xl font-bold mb-2">Finance Conversion Engine</h2>
            <p className="text-gray-600">Analysis ID: {analysis.analysis_id}</p>
            <p className="text-sm text-gray-500">Generated: {new Date(analysis.generated_at).toLocaleString()}</p>
            {analysis.ai_narratives?.generated_by === 'GPT-5.1' && (
              <div className="mt-2 flex items-center space-x-2 text-sm text-purple-600">
                <Sparkles className="w-4 h-4" />
                <span>AI-powered insights by GPT-5.1</span>
              </div>
            )}
          </div>
          <button
            onClick={downloadReport}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Download className="w-4 h-4" />
            <span>Export Report</span>
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white">
          <Target className="w-10 h-10 mb-3 opacity-80" />
          <div className="text-3xl font-bold mb-2">{analysis.summary.total_domains}</div>
          <div className="text-sm opacity-90">Finance Domains</div>
        </div>
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white">
          <AlertTriangle className="w-10 h-10 mb-3 opacity-80" />
          <div className="text-3xl font-bold mb-2">{analysis.summary.total_gaps}</div>
          <div className="text-sm opacity-90">Conversion Gaps</div>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white">
          <Calendar className="w-10 h-10 mb-3 opacity-80" />
          <div className="text-3xl font-bold mb-2">{analysis.summary.total_effort_months}M</div>
          <div className="text-sm opacity-90">Effort ({analysis.summary.total_effort_days} days)</div>
        </div>
        <div className={`bg-gradient-to-br rounded-xl p-6 text-white ${
          analysis.summary.overall_risk_level === 'HIGH' ? 'from-red-500 to-red-600' :
          analysis.summary.overall_risk_level === 'MEDIUM' ? 'from-yellow-500 to-yellow-600' :
          'from-green-500 to-green-600'
        }`}>
          <AlertTriangle className="w-10 h-10 mb-3 opacity-80" />
          <div className="text-3xl font-bold mb-2">{analysis.summary.overall_risk_level}</div>
          <div className="text-sm opacity-90">Risk Level ({analysis.summary.total_risks} risks)</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-md">
        <div className="border-b overflow-x-auto">
          <div className="flex">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-blue-600 font-semibold'
                      : 'border-transparent text-gray-600 hover:text-blue-600 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-bold mb-4">Critical Domains</h3>
                <div className="flex flex-wrap gap-2">
                  {analysis.summary.critical_domains.map((domain) => (
                    <span key={domain} className="px-4 py-2 bg-red-100 text-red-800 rounded-lg font-semibold">
                      {domainNames[domain] || domain}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold mb-4">Conversion Summary</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">{analysis.summary.total_waves}</div>
                    <div className="text-sm text-gray-600">Conversion Waves</div>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600">{analysis.summary.total_test_scenarios}</div>
                    <div className="text-sm text-gray-600">Test Scenarios</div>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">
                      {analysis.reconciliation_plan.reconciliation_points.length}
                    </div>
                    <div className="text-sm text-gray-600">Reconciliation Points</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Configuration Tab */}
          {activeTab === 'config' && (
            <div className="space-y-6">
              <h3 className="text-xl font-bold mb-4">Current Finance Configuration</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {Object.entries(analysis.current_finance_model.modules).map(([key, module]) => {
                  const Icon = domainIcons[key] || Database;
                  return (
                    <div key={key} className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-400 transition-colors">
                      <div className="flex items-center space-x-3 mb-3">
                        <Icon className="w-6 h-6 text-blue-600" />
                        <h4 className="font-bold">{domainNames[key] || key}</h4>
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        {Object.entries(module).slice(1, 5).map(([field, value]) => (
                          <div key={field} className="flex justify-between">
                            <span className="capitalize">{field.replace(/_/g, ' ')}:</span>
                            <span className="font-semibold">
                              {typeof value === 'object' ? JSON.stringify(value).slice(0, 30) + '...' : String(value)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Domain Assessments Tab */}
          {activeTab === 'domains' && (
            <div className="space-y-6">
              <div className="flex space-x-2 overflow-x-auto pb-2">
                {Object.keys(analysis.domain_assessments).map((domain) => (
                  <button
                    key={domain}
                    onClick={() => setSelectedDomain(domain)}
                    className={`px-4 py-2 rounded-lg whitespace-nowrap ${
                      selectedDomain === domain
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {domainNames[domain] || domain}
                  </button>
                ))}
              </div>

              {selectedDomain && analysis.domain_assessments[selectedDomain] && (
                <div className="space-y-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-bold mb-2">Assessment Summary</h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Total Effort</p>
                        <p className="text-2xl font-bold text-blue-600">
                          {analysis.domain_assessments[selectedDomain].total_effort_days} days
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Priority</p>
                        <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                          analysis.domain_assessments[selectedDomain].priority === 'critical'
                            ? 'bg-red-100 text-red-800'
                            : analysis.domain_assessments[selectedDomain].priority === 'high'
                            ? 'bg-orange-100 text-orange-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {analysis.domain_assessments[selectedDomain].priority.toUpperCase()}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-bold mb-3">Conversion Gaps</h4>
                    <div className="space-y-2">
                      {analysis.domain_assessments[selectedDomain].gaps.map((gap, index) => (
                        <div key={index} className="p-3 border-2 border-gray-200 rounded-lg">
                          <div className="flex justify-between items-start mb-1">
                            <span className="font-semibold text-sm">{gap.title}</span>
                            <span className={`px-2 py-1 rounded text-xs ${
                              gap.complexity === 'high' ? 'bg-red-100 text-red-800' :
                              gap.complexity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-green-100 text-green-800'
                            }`}>
                              {gap.complexity}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600">Effort: {gap.effort_days} days</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-bold mb-3">Risks</h4>
                    <div className="space-y-2">
                      {analysis.domain_assessments[selectedDomain].risks.map((risk, index) => (
                        <div key={index} className="p-3 bg-red-50 border-2 border-red-200 rounded-lg">
                          <div className="flex items-start space-x-2">
                            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <p className="font-semibold text-sm">{risk.title}</p>
                              <p className="text-xs text-gray-600">Severity: {risk.severity}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Gap Analysis Tab */}
          {activeTab === 'gaps' && (
            <div className="space-y-6">
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <div className="p-4 bg-red-50 rounded-lg">
                  <div className="text-2xl font-bold text-red-600">{analysis.gap_analysis.gaps_by_complexity.high}</div>
                  <div className="text-sm text-gray-600">High Complexity</div>
                </div>
                <div className="p-4 bg-yellow-50 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-600">{analysis.gap_analysis.gaps_by_complexity.medium}</div>
                  <div className="text-sm text-gray-600">Medium Complexity</div>
                </div>
                <div className="p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">{analysis.gap_analysis.gaps_by_complexity.low}</div>
                  <div className="text-sm text-gray-600">Low Complexity</div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Gap</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Domain</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Complexity</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Effort (days)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {analysis.gap_analysis.gaps.map((gap, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm">{gap.title}</td>
                        <td className="px-4 py-3 text-sm">{domainNames[gap.domain] || gap.domain}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-xs ${
                            gap.complexity === 'high' ? 'bg-red-100 text-red-800' :
                            gap.complexity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {gap.complexity}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold">{gap.effort_days}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Risk Profile Tab */}
          {activeTab === 'risks' && (
            <div className="space-y-6">
              <div className="p-6 bg-gradient-to-r from-red-50 to-orange-50 rounded-lg">
                <h4 className="font-bold mb-2">Overall Risk Assessment</h4>
                <div className="flex items-center space-x-4">
                  <div className="text-4xl font-bold text-red-600">{analysis.risk_profile.overall_risk_score}</div>
                  <div>
                    <p className="font-semibold">{analysis.risk_profile.overall_risk_level} Risk Level</p>
                    <p className="text-sm text-gray-600">Based on {analysis.risk_profile.total_risks} identified risks</p>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-4 gap-4">
                <div className="p-4 bg-red-50 rounded-lg">
                  <div className="text-2xl font-bold text-red-600">{analysis.risk_profile.risks_by_severity.critical}</div>
                  <div className="text-sm text-gray-600">Critical Risks</div>
                </div>
                <div className="p-4 bg-orange-50 rounded-lg">
                  <div className="text-2xl font-bold text-orange-600">{analysis.risk_profile.risks_by_severity.high}</div>
                  <div className="text-sm text-gray-600">High Risks</div>
                </div>
                <div className="p-4 bg-yellow-50 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-600">{analysis.risk_profile.risks_by_severity.medium}</div>
                  <div className="text-sm text-gray-600">Medium Risks</div>
                </div>
                <div className="p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">{analysis.risk_profile.risks_by_severity.low}</div>
                  <div className="text-sm text-gray-600">Low Risks</div>
                </div>
              </div>

              <div>
                <h4 className="font-bold mb-3">Risk Details</h4>
                <div className="space-y-2">
                  {analysis.risk_profile.risks.slice(0, 10).map((risk, index) => (
                    <div key={index} className="p-3 border-2 border-gray-200 rounded-lg hover:border-red-400">
                      <div className="flex justify-between items-start mb-2">
                        <span className="font-semibold text-sm">{risk.title}</span>
                        <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
                          Score: {risk.risk_score}
                        </span>
                      </div>
                      <div className="flex space-x-4 text-xs text-gray-600">
                        <span>Likelihood: {risk.likelihood}/5</span>
                        <span>Impact: {risk.impact}/5</span>
                        <span className="capitalize">Severity: {risk.severity}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Backlog Tab */}
          {activeTab === 'backlog' && (
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 rounded-lg mb-4">
                <h4 className="font-bold mb-2">Conversion Backlog Summary</h4>
                <p className="text-sm text-gray-600">
                  {analysis.conversion_backlog.length} work items · {analysis.summary.total_effort_days} total effort days
                </p>
              </div>

              <div className="space-y-2">
                {analysis.conversion_backlog.map((item, index) => (
                  <div key={index} className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-400">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <span className="font-semibold">{item.title}</span>
                        <p className="text-xs text-gray-500 mt-1">{item.backlog_id}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        item.priority === 'critical' ? 'bg-red-100 text-red-800' :
                        item.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {item.priority}
                      </span>
                    </div>
                    <div className="flex space-x-4 text-sm text-gray-600">
                      <span>Domain: {domainNames[item.domain] || item.domain}</span>
                      <span>Effort: {item.effort_days} days</span>
                      <span className="capitalize">Complexity: {item.complexity}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Waves Tab */}
          {activeTab === 'waves' && (
            <div className="space-y-4">
              <div className="p-4 bg-purple-50 rounded-lg mb-4">
                <h4 className="font-bold mb-2">8-Wave Conversion Plan</h4>
                <p className="text-sm text-gray-600">
                  Total duration: {analysis.conversion_waves.reduce((sum, w) => sum + w.duration_weeks, 0)} weeks
                </p>
              </div>

              {analysis.conversion_waves.map((wave, index) => (
                <div key={index} className="border-2 border-gray-200 rounded-lg overflow-hidden">
                  <div className={`p-4 bg-gradient-to-r ${
                    index % 4 === 0 ? 'from-blue-500 to-blue-600' :
                    index % 4 === 1 ? 'from-purple-500 to-purple-600' :
                    index % 4 === 2 ? 'from-green-500 to-green-600' :
                    'from-orange-500 to-orange-600'
                  } text-white`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-bold text-lg">Wave {wave.wave}: {wave.name}</h4>
                        <p className="text-sm opacity-90 mt-1">
                          Duration: {wave.duration_weeks} weeks · {wave.backlog_items} items · {wave.total_effort_days} effort days
                        </p>
                      </div>
                      <Calendar className="w-8 h-8 opacity-80" />
                    </div>
                  </div>
                  {wave.tasks.length > 0 && (
                    <div className="p-4 bg-gray-50">
                      <h5 className="font-semibold mb-2 text-sm">Tasks in this wave:</h5>
                      <div className="space-y-1">
                        {wave.tasks.slice(0, 3).map((task, taskIndex) => (
                          <div key={taskIndex} className="text-sm text-gray-600">
                            • {task.title} ({task.effort_days} days)
                          </div>
                        ))}
                        {wave.tasks.length > 3 && (
                          <div className="text-sm text-gray-500 italic">
                            + {wave.tasks.length - 3} more tasks...
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Testing & Reconciliation Tab */}
          {activeTab === 'testing' && (
            <div className="space-y-6">
              <div>
                <h4 className="font-bold mb-4">Reconciliation Points</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  {analysis.reconciliation_plan.reconciliation_points.map((point, index) => (
                    <div key={index} className="p-4 border-2 border-gray-200 rounded-lg">
                      <h5 className="font-semibold mb-2">{point.title}</h5>
                      <div className="text-sm text-gray-600 space-y-1">
                        <p>Type: {point.type}</p>
                        <p>Frequency: {point.frequency}</p>
                        <p className={`inline-block px-2 py-1 rounded text-xs ${
                          point.complexity === 'high' ? 'bg-red-100 text-red-800' :
                          point.complexity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {point.complexity} complexity
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-bold mb-4">Test Scenarios</h4>
                <div className="space-y-2">
                  {analysis.test_scenarios.map((scenario, index) => (
                    <div key={index} className="p-4 border-2 border-gray-200 rounded-lg hover:border-green-400">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <span className="font-semibold">{scenario.title}</span>
                          <p className="text-xs text-gray-500 mt-1">{scenario.scenario_id}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          scenario.priority === 'critical' ? 'bg-red-100 text-red-800' :
                          scenario.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {scenario.priority}
                        </span>
                      </div>
                      <div className="flex space-x-4 text-sm text-gray-600">
                        <span>Type: {scenario.test_type}</span>
                        <span>{scenario.test_cases} test cases</span>
                        <span>{scenario.estimated_hours} hours</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* AI Insights Tab */}
          {activeTab === 'ai-insights' && analysis.ai_narratives && (
            <div className="space-y-6">
              <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg flex items-center space-x-3">
                <Sparkles className="w-6 h-6 text-purple-600" />
                <div>
                  <h4 className="font-bold">AI-Generated Insights</h4>
                  <p className="text-sm text-gray-600">
                    Powered by {analysis.ai_narratives.generated_by} · Generated {new Date(analysis.ai_narratives.generated_at).toLocaleString()}
                  </p>
                </div>
              </div>

              <div className="bg-white border-2 border-blue-200 rounded-lg p-6">
                <h4 className="font-bold text-xl mb-4 flex items-center space-x-2">
                  <Briefcase className="w-6 h-6 text-blue-600" />
                  <span>CFO Executive Summary</span>
                </h4>
                <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap">
                  {analysis.ai_narratives.cfo_summary}
                </div>
              </div>

              <div className="bg-white border-2 border-purple-200 rounded-lg p-6">
                <h4 className="font-bold text-xl mb-4 flex items-center space-x-2">
                  <Building className="w-6 h-6 text-purple-600" />
                  <span>Solution Architect Rationale</span>
                </h4>
                <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap">
                  {analysis.ai_narratives.architect_rationale}
                </div>
              </div>

              {analysis.ai_narratives.context_used && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h5 className="font-semibold mb-2 text-sm">Context Used for AI Generation:</h5>
                  <div className="grid md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">Total Gaps</p>
                      <p className="font-bold">{analysis.ai_narratives.context_used.total_gaps}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Total Risks</p>
                      <p className="font-bold">{analysis.ai_narratives.context_used.total_risks}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Effort Days</p>
                      <p className="font-bold">{analysis.ai_narratives.context_used.effort_days}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Risk Level</p>
                      <p className="font-bold">{analysis.ai_narratives.context_used.risk_level}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FinanceConversion;